import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "segmentSelect",
    "segmentInput",
    "subscriptionFilter",
    "subscriptionFilterSelect",
    "specificSubscriptionTypes",
    "subscriptionTypeCheckbox",
    "subscriptionTypeCard",
    "purchaseFilter",
    "purchaseFilterCheckbox",
    "purchaseFilterCard",
    "purchaseItemCheckbox",
    "purchaseItemCard"
  ];

  static values = {
    purchasableItemsUrl: String
  };
  
  connect() {
    this.updateVisibility();
    this.updateOptions();
  }
  
  updateSegment() {
    this.updateVisibility();
    this.updateOptions();
  }
  
  updateVisibility() {
    const selectedSegment = this.getSelectedSegment();
    this.clearFilters(selectedSegment);
    if (selectedSegment === "members_only") {
      this.showSubscriptionFilter();
    }else if (selectedSegment === "purchasers_only"){
      this.showPurchaseFilter();
    }
  }
  
  clearFilters(selectedSegment){
    switch(selectedSegment){
      case "members_only":
        this.hidePurchaseFilter();
        break;
      case "purchasers_only":
        this.hideSubscriptionFilter();
        break;
      case "all":
        this.hidePurchaseFilter();
        this.hideSubscriptionFilter();
        break;
    }
  }
  
  updateOptions() {
    this.segmentInputTargets.forEach(input => {
      const card = input.closest(".bulk-email-segment__option");
      if(card){
        card.classList.toggle("bulk-email-segment__option--selected", input.checked)
      }
    })
  }

  //Method triggered by Stimulus on subscription filter selection change
  updateSubscriptionFilter() {
    this.updateSpecificSubscriptionTypesVisibility();
  }

  //Method triggered by Stimulus on purchase filter selection change
  updatePurchaseFilter(event) {
    this.updateSpecificPurchaseTypesVisibility();
    this.updateSpecificPurchaseItemsVisibility();

    if (event?.type === "change") {
      this.reloadPurchasableItems();
    }
  }

  reloadPurchasableItems(fetch_all = false, select_all = false) {
    if (!this.hasPurchasableItemsUrlValue) return;

    const frame = document.getElementById("bulk_email_purchasable_items");
    if (!frame) return;

    const url = new URL(this.purchasableItemsUrlValue, window.location.origin);
    url.searchParams.set("products_page", "1");
    url.searchParams.set("fetch_all", fetch_all)
    url.searchParams.set("select_all", select_all)
    this.getSelectedPurchaseFilter().forEach(type => {
      url.searchParams.append("product_types[]", type);
    });

    this.getSelectedPurchaseItemIds().forEach(id => {
      url.searchParams.append("selected_product_ids[]", id);
    });

    frame.src = url.toString();
    frame.reload();
  }

  updateSpecificPurchaseTypesVisibility() {
    this.purchaseFilterCardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        card.classList.add('bulk-email-segment__select-purchase-card--selected');
      } else {
        card.classList.remove('bulk-email-segment__select-purchase-card--selected');
      }
    })
  }
  
  updateSpecificPurchaseItemsVisibility(){
    //Iterate through purchase item sections and toggle on if selected and off if not selected
    const purchase_filters = this.getSelectedPurchaseFilter();
    this.purchaseItemCardTargets.forEach(card => {
      if(purchase_filters.includes(card.dataset.purchaseType)){
        card.style.display = "";
      }else{
        card.style.display = "none";
        card.querySelector('input[type="checkbox"]').checked = false;
      }
    });
  }

  updateSpecificTypes() {
    this.updateSpecificMemberTypes();
  }


  updateSpecificMemberTypes() {
    // Update card styling based on checkbox state
    this.subscriptionTypeCardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) {
        card.classList.add('bulk-email-segment__type-card--selected');
      } else {
        card.classList.remove('bulk-email-segment__type-card--selected');
      }
    });
  }

  updateSpecificSubscriptionTypesVisibility() {
    const selectedFilter = this.getSelectedSubscriptionFilter();
    
    if (selectedFilter === "specific" && this.hasSpecificSubscriptionTypesTarget) {
      this.specificSubscriptionTypesTarget.style.display = "flex";
    } else if (this.hasSpecificSubscriptionTypesTarget) {
      this.specificSubscriptionTypesTarget.style.display = "none";
      // Uncheck all subscription type checkboxes when hiding
      this.subscriptionTypeCheckboxTargets.forEach(checkbox => {
        checkbox.checked = false;
      });
      this.updateSpecificMemberTypes();
    }
  }
  


  showPurchaseFilter() {
    if (this.hasPurchaseFilterTarget) {
      this.purchaseFilterTarget.style.display = "flex";
    }
  }

  hidePurchaseFilter() {
    if (this.hasPurchaseFilterTarget) {
      this.purchaseFilterTarget.style.display = "none";
    }
    this.purchaseItemCheckboxTargets.forEach(box => box.checked=false);
  }
  
  showSubscriptionFilter() {
    if (this.hasSubscriptionFilterTarget) {
      this.subscriptionFilterTarget.style.display = "flex";
    }
    this.updateSpecificSubscriptionTypesVisibility();
  }

  hideSubscriptionFilter() {
    if (this.hasSubscriptionFilterTarget) {
      this.subscriptionFilterTarget.style.display = "none";
    }
    if (this.hasSpecificSubscriptionTypesTarget) {
      this.specificSubscriptionTypesTarget.style.display = "none";
    }
    // Reset subscription filter to "all" when hiding
    if (this.hasSubscriptionFilterSelectTarget) {
      this.subscriptionFilterSelectTarget.value = "all";
    }
    // Uncheck all subscription type checkboxes
    this.subscriptionTypeCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false;
    });
    this.updateSpecificMemberTypes();
  }

  getSelectedSegment() {
    const selectedInput = this.segmentInputTargets.find(input => input.checked);
    if (selectedInput) {
      return selectedInput.value;
    }
    return "all";
  }

  getSelectedSubscriptionFilter() {
    const selectedInput = this.subscriptionFilterSelectTargets.find(input => input.checked);
    return selectedInput?.value || "all";
  }

  getSelectedPurchaseFilter() {
    return this.purchaseFilterCheckboxTargets.filter(input => input.checked).map(input => input.value);
  }

  getSelectedSubscriptionTypeIds() {
    return this.subscriptionTypeCheckboxTargets
      .filter(input => input.checked)
      .map(input => input.value);
  }

  getSelectedPurchaseItemIds() {
    return this.purchaseItemCheckboxTargets
      .filter(input => input.checked)
      .map(input => input.value);
  }

  selectAllPurchaseItems() {
    this.reloadPurchasableItems(true, true);
  }

  deselectAllVisiblePurchaseItems() {
    const purchase_filters = this.getSelectedPurchaseFilter();
    this.purchaseItemCardTargets.forEach(card => {
      if(purchase_filters.includes(card.dataset.purchaseType)){
        card.querySelector('input[type="checkbox"]').checked = false;
      }
    }); 
  }
}
